import type { Level1Details } from '@/features/fieldSlice';
import type { FilterState } from '@/features/filterSlice';
import type { NetworkType, Rating, WildfireRiskRating } from '@/features/types';
import { PriorityRatingType } from '@/features/types';

export const prepareFilterData = (data: string[]) => {
  return data
    .filter((v, i, a) => a.indexOf(v) === i)
    .sort()
    .map((i: string) => ({
      value: i,
      label: i?.trim() ? i : 'N/A',
    }));
};

export const getLevel2Data = (
  data: { level1: string; level2: number[] | string[] }[] | undefined,
  selectedLevel1: string[] = []
) => {
  if (!data) return [];

  return prepareFilterData(
    data
      .filter(
        (i) => selectedLevel1.length === 0 || selectedLevel1.includes(i.level1)
      )
      .flatMap<number | string>((i) => i.level2)
      .map((i) => i.toString())
  );
};

export const getLevelFieldData = (
  networkType: NetworkType,
  level1FieldData: Record<number, Level1Details[]> | undefined,
  level2FieldData: string[] | undefined,
  hasMultipleNetworks = false
) => {
  if (level1FieldData) {
    return level1FieldData[networkType];
  }

  if (level2FieldData) {
    return [
      {
        level1: '',
        level2: hasMultipleNetworks
          ? [level2FieldData[networkType]]
          : level2FieldData,
      },
    ];
  }

  return [];
};

export const groupIdsInFilter = (filter: FilterState): string[] => {
  const groupIds: string[] = [];
  for (const [groupId, filteredGroupNames] of Object.entries(filter.groups)) {
    if (filteredGroupNames && filteredGroupNames.length > 0) {
      groupIds.push(groupId);
    }
  }

  return groupIds;
};

export const getRatingOptions = (
  cls: typeof Rating | typeof PriorityRatingType | typeof WildfireRiskRating
) => {
  const options = Object.entries(cls)
    .filter(([_, value]) => !isNaN(Number(value)))
    .map(([key, value]) => ({
      value: `${value}`,
      label: key.replace('_', ' '),
    }));

  if (cls === PriorityRatingType) {
    return options.sort((a, b) => {
      // Default: Put Unknown at the end, sort others by numeric value
      if (a.label === 'Unknown') return 1;
      if (b.label === 'Unknown') return -1;
      return Number(a.value) - Number(b.value);
    });
  } else {
    return options.reverse();
  }
};

export const filterFalsyValues = <T>(
  obj: Record<string, T>
): Record<string, NonNullable<T>> =>
  Object.fromEntries(
    Object.entries(obj).filter((entry): entry is [string, NonNullable<T>] => {
      const value = entry[1];
      if (Array.isArray(value)) {
        return value.length > 0;
      } else {
        return value !== undefined && value !== null;
      }
    })
  );
